
import { useState } from 'react';
import { downloadableGuides } from '../../../mocks/resources';

export default function DownloadableGuides() {
  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', 'Business', 'HVAC', 'Plumbing', 'Electrical', 'Carpentry', 'Safety'];

  const filteredGuides = activeCategory === 'All'
    ? downloadableGuides
    : downloadableGuides.filter(guide => guide.category === activeCategory);

  const handleDownload = (guideTitle: string) => {
    // Simulate download - in production, this would trigger actual file download
    console.log(`Downloading: ${guideTitle}`);
  };

  return (
    <section id="guides" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
            <i className="ri-file-download-line text-amber-600" />
            <span className="text-amber-700 text-sm font-medium">Free Downloads</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Download Expert <span className="text-amber-600">Guides</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            PDF resources ready for offline access. Master invoicing, compliance, and trade-specific best practices.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-5 py-2.5 rounded-full font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeCategory === category
                  ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-amber-300 hover:text-amber-600'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Guides Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredGuides.map((guide, index) => (
            <div
              key={guide.id}
              className="group bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-xl hover:border-amber-200 transition-all duration-500"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Image */}
              <div className="relative h-44 overflow-hidden">
                <img
                  src={guide.image}
                  alt={guide.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full text-xs font-medium text-slate-700">
                    <i className={`${guide.icon} text-amber-500`} />
                    {guide.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-amber-600 transition-colors">
                  {guide.title}
                </h3>
                <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                  {guide.description}
                </p>

                {/* Meta */}
                <div className="flex items-center justify-between text-xs text-slate-400 mb-4">
                  <span>{guide.pages} pages</span>
                  <span>{guide.fileSize}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-download-2-line" />
                    {guide.downloadCount.toLocaleString()}
                  </span>
                </div>

                {/* Download Button */}
                <button
                  onClick={() => handleDownload(guide.title)}
                  className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl font-medium hover:from-amber-600 hover:to-amber-700 transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-download-line" />
                  Download PDF
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* View All Link */}
        <div className="text-center mt-12">
          <button className="inline-flex items-center gap-2 px-8 py-4 border-2 border-amber-500 text-amber-600 rounded-full font-semibold hover:bg-amber-500 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap">
            <span>View All 45+ Guides</span>
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>
    </section>
  );
}
